import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { TabPanel } from "../../../../../../../components/TabPanel";
import { Shape } from "../../RampCanvasV2";
import { TAG_OPTIONS } from "../constants";

type Props = {
  value: number;
  index: number;
  shape: Shape | undefined;
  hasMixedTypes: boolean;
  hasMixedValue: (key: keyof Shape) => boolean;
  setSelectedShapes: (attribute: string, value: any) => void;
};

export const PropertiesTab: React.FC<Props> = ({
  value,
  index,
  shape,
  hasMixedTypes,
  hasMixedValue,
  setSelectedShapes,
}) => {
  const { t } = useTranslation();

  if (!shape) return null;

  return (
    <TabPanel value={value} index={index}>
      <Stack direction="column" spacing={1}>
        {hasMixedTypes && (
          <Alert severity="info" sx={{ py: 0.25, "& .MuiAlert-message": { fontSize: "0.75rem" } }}>
            {t('settings.rampsSettings.rampEditor.shapeEdit.mixedTypes', 'Editing multiple shape types. Some options may not apply to all shapes.')}
          </Alert>
        )}
        <FormControlLabel
          control={<Switch size="small" checked={shape?.locked ?? false} />}
          onChange={() => setSelectedShapes("locked", !shape?.locked)}
          sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {t('settings.rampsSettings.rampEditor.shapeEdit.properties.locked')}
              {hasMixedValue("locked") && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                  ({t('common.mixed', 'mixed')})
                </Typography>
              )}
            </Box>
          }
        />
        <TextField
          size="small"
          fullWidth
          label={t('settings.rampsSettings.rampEditor.shapeEdit.properties.tags')}
          select
          onChange={(event) => {
            const value = event.target.value;
            setSelectedShapes(
              "tags",
              typeof value === "string" ? value.split(",") : value
            );
          }}
          value={shape?.tags ?? []}
          SelectProps={{
            multiple: true,
            renderValue: (selected) => {
              if (Array.isArray(selected)) {
                return selected.join(", ");
              }
              return String(selected);
            },
            MenuProps: {
              PaperProps: {
                sx: { zIndex: 9999 },
              },
            },
          }}
        >
          {TAG_OPTIONS.map((tag) => (
            <MenuItem key={tag} value={tag}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shape?.tags?.includes(tag) || false}
                    onChange={(e) => {
                      const currentTags = [...(shape?.tags || [])];
                      if (e.target.checked) {
                        if (!currentTags.includes(tag)) {
                          setSelectedShapes("tags", [...currentTags, tag]);
                        }
                      } else {
                        setSelectedShapes(
                          "tags",
                          currentTags.filter((t) => t !== tag)
                        );
                      }
                    }}
                  />
                }
                label={tag}
                onClick={(e) => e.stopPropagation()}
              />
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label={t('settings.rampsSettings.rampEditor.shapeEdit.properties.text')}
          fullWidth
          onChange={(event) => {
            setSelectedShapes("text", event.target.value);
          }}
          value={hasMixedValue("text") ? "" : (shape?.text || "")}
          placeholder={hasMixedValue("text") ? t('common.multipleValues', 'Multiple values') : undefined}
        />
      </Stack>
    </TabPanel>
  );
};
